import React, { useEffect, useState, useRef } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { NavLink, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";

import { useUiFeedback } from "../../../context/ui-feedback/ui-feedback";
import {
  createWaiting,
  closeWaiting,
  createErrorFeedback,
  createSuccessFeedback,
} from "../../../context/ui-feedback/action";
import { useTemplates } from "../../../context/templates/templates";
import { updateTemplateSearchCriteria } from "../../../context/templates/action";
import { PageLayout } from "../../common/page-layout/page-layout";
import { ControllerRow } from "../../common/controller-row/controller-row";
import { TemplateSearch } from "./component/template-search";
import { TemplateTable } from "./component/template-table";
import { Pager } from "../../common/pager/pager";
import {
  useSearchTemplates,
  useCopyTemplate,
  useDeleteTemplateById,
} from "../../../api/templates-api";

export const Templates = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { uiFeedbackDispatch } = useUiFeedback();

  const {
    dispatch,
    state: { search },
  } = useTemplates();

  const searchTemplates = useSearchTemplates();
  const copyTemplate = useCopyTemplate();
  const deleteTemplateById = useDeleteTemplateById();

  const localSearchUUID = useRef(null);
  const [results, setResults] = useState({
    allCount: 0,
    templates: null,
  });
  const [errorInLoading, setErrorInLoading] = useState(false);

  useEffect(() => {
    const updateResultsList = async (uuid) => {
      setErrorInLoading(false);
      setResults({
        ...results,
        templates: null,
      });
      try {
        await searchTemplates(search, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            if (uuid === localSearchUUID.current) {
              if (data.templates.length === 0 && data.allCount > 0) {
                updateTemplateSearchCriteria(
                  dispatch,
                  search.deleted,
                  search.draft,
                  search.name,
                  (Math.ceil(data.allCount / search.limit) - 1) * search.limit,
                  search.limit,
                );
              } else {
                setResults(data);
              }
            }
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          setErrorInLoading(true);
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.templates.exception.listtemplate.title",
            }),
            intl.formatMessage({
              id: "page.templates.exception.listtemplate.description",
            }),
          );
        }
      }
    };

    const checkNotEmpy = (items) => {
      return items.filter((item) => item === null).length === 0;
    };

    if (
      (!results || search.uuid !== localSearchUUID.current) &&
      checkNotEmpy([
        dispatch,
        intl,
        navigate,
        searchTemplates,
        uiFeedbackDispatch,
      ])
    ) {
      const uuid = search.uuid;
      localSearchUUID.current = uuid;
      updateResultsList(uuid);
    }
  }, [
    results,
    search,
    localSearchUUID,
    dispatch,
    intl,
    navigate,
    searchTemplates,
    uiFeedbackDispatch,
  ]);

  const changePage = (page) => {
    updateTemplateSearchCriteria(
      dispatch,
      search.deleted,
      search.draft,
      search.name,
      page * search.limit,
      search.limit,
    );
  };

  const handleCopyTemplate = async (templateCopy) => {
    try {
      createWaiting(uiFeedbackDispatch);
      await copyTemplate(templateCopy, (data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          createSuccessFeedback(
            uiFeedbackDispatch,
            intl.formatMessage(
              { id: "page.templates.success.copytemplate" },
              { name: templateCopy.name },
            ),
          );
          navigate(`/edittemplate/${data.id}`);
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({
            id: "page.templates.exception.copytemplate.title",
          }),
          intl.formatMessage(
            { id: "page.templates.exception.copytemplate.description" },
            { name: templateCopy.name },
          ),
        );
      }
    } finally {
      closeWaiting(uiFeedbackDispatch);
      updateTemplateSearchCriteria(
        dispatch,
        search.deleted,
        search.draft,
        search.name,
        search.offset,
        search.limit,
      );
    }
  };

  const deleteTemplate = async (template) => {
    try {
      createWaiting(uiFeedbackDispatch);
      await deleteTemplateById(template.id, (data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          createSuccessFeedback(
            uiFeedbackDispatch,
            intl.formatMessage(
              { id: "page.templates.success.delete" },
              { name: template.name },
            ),
          );
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({ id: "page.templates.exception.delete.title" }),
          intl.formatMessage(
            { id: "page.templates.exception.delete.description" },
            { name: template.name },
          ),
        );
      }
    } finally {
      closeWaiting(uiFeedbackDispatch);
      updateTemplateSearchCriteria(
        dispatch,
        search.deleted,
        search.draft,
        search.name,
        search.offset,
        search.limit,
      );
    }
  };

  const handleSearch = (searchParam) => {
    updateTemplateSearchCriteria(
      dispatch,
      searchParam.deleted,
      searchParam.draft,
      searchParam.name,
      0,
      search.limit,
    );
  };

  const buildPaths = () => {
    return [
      {
        link: "/",
        title: intl.formatMessage({ id: "page.welcome.title" }),
      },
    ];
  };

  return (
    <PageLayout
      paths={buildPaths()}
      title={intl.formatMessage({ id: "page.templates.title" })}
    >
      <>
        <ControllerRow>
          <>
            <div className="text-end mb-2">
              <Button variant="outline-primary" as={NavLink} to="/newtemplate">
                <FormattedMessage id="page.templates.button.newtemplate" />
              </Button>
            </div>
            <TemplateSearch
              name={search.name}
              deleted={search.deleted}
              draft={search.draft}
              handleSearch={handleSearch}
            />
          </>
        </ControllerRow>
        <Pager
          currentPage={search.offset / search.limit}
          allPage={Math.ceil(results?.allCount / search.limit)}
          topPager={true}
          bottomPager={true}
          handleChangePage={changePage}
        >
          <TemplateTable
            rowNumberInit={search.offset}
            templates={results?.templates}
            handleTemplateCopy={(templateCopy) => {
              handleCopyTemplate(templateCopy);
            }}
            handleTemplateDelete={(template) => {
              deleteTemplate(template);
            }}
            hasError={errorInLoading}
          />
        </Pager>
      </>
    </PageLayout>
  );
};
